import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Navbar from '../components/Navbar';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import './DocumentEditor.css';

const WORD_LIMIT = 3000;

const DocumentEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [matrice, setMatrice] = useState(null);
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewBackgroundColor, setPreviewBackgroundColor] = useState(isDarkMode ? '#2d2d2d' : '#ffffff');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [loadingMatrices, setLoadingMatrices] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      const matricesList = await loadMatrices(id);
      if (id) {
        await loadDocument(id, matricesList);
      }
    };
    loadData();
  }, [id]);

  // Mettre à jour la couleur de fond de l'aperçu quand le dark mode change
  useEffect(() => {
    setPreviewBackgroundColor(isDarkMode ? '#2d2d2d' : '#ffffff');
  }, [isDarkMode]);

  const loadMatrices = async (documentId) => {
    try {
      setLoadingMatrices(true);
      const response = await api.get('/matrices/');
      const matricesList = response.data.results || response.data;
      setMatrices(matricesList);
      
      // Sélectionner la dernière matrice par défaut UNIQUEMENT si on crée un nouveau document
      if (!documentId && matricesList.length > 0) {
        setMatrice(matricesList[0]);
      }
      return matricesList;
    } catch (error) {
      console.error('Error loading matrices:', error);
      return [];
    } finally {
      setLoadingMatrices(false);
    }
  };

  const loadDocument = async (documentId, matricesList = matrices) => {
    try {
      const response = await api.get(`/documents/${documentId}/`);
      if (response.data) {
        setTitle(response.data.titre || '');
        setContent(response.data.contenu || '');
        
        // Charger la matrice associée
        if (response.data.matrice_details) {
          setMatrice(response.data.matrice_details);
        } else if (response.data.matrice_utilisee) {
          // Si on a juste l'ID, chercher dans les matrices déjà chargées
          const foundMatrice = matricesList.find(m => m.id === response.data.matrice_utilisee);
          if (foundMatrice) {
            setMatrice(foundMatrice);
          } else {
            // Sinon, charger la matrice complète depuis l'API
            try {
              const matriceResponse = await api.get(`/matrices/${response.data.matrice_utilisee}/`);
              setMatrice(matriceResponse.data);
            } catch (err) {
              console.error('Error loading matrice:', err);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
      alert(t('document.loadError') || 'Erreur lors du chargement du document');
    }
  };

  const handleMatriceChange = (e) => {
    const selectedId = parseInt(e.target.value);
    const selectedMatrice = matrices.find(m => m.id === selectedId);
    setMatrice(selectedMatrice);
  };

  const getWordCount = (htmlContent) => {
    if (!htmlContent || htmlContent === '<p><br></p>') return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleContentChange = (newContent) => {
    const newWordCount = getWordCount(newContent);
    if (newWordCount <= WORD_LIMIT) {
      setContent(newContent);
    }
  };

  const handleCharacterClick = (char) => {
    if (getWordCount(content) >= WORD_LIMIT) return;
    // Insérer le caractère dans l'éditeur Quill
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      if (range) {
        editor.insertText(range.index, char);
        editor.setSelection(range.index + 1);
      } else {
        editor.insertText(editor.getLength() - 1, char);
      }
    }
  };

  const applyColorsToText = (htmlContent) => {
    if (!matrice) return htmlContent;
    
    const lettresCouleurs = matrice.lettres_couleurs || {};
    const caracteresSpeciauxCouleurs = matrice.caracteres_speciaux_couleurs || {};
    const chiffresCouleurs = matrice.chiffres_couleurs || {};
    
    // Créer un div temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Fonction récursive pour colorer uniquement les nœuds texte
    const colorTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent;
        let hasColors = false;
        let styledHTML = '';
        
        // Construire le HTML avec les couleurs caractère par caractère
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const charUpper = char.toUpperCase();
          const charLower = char.toLowerCase();
          
          // Vérifier si c'est un caractère spécial avec couleur
          if (caracteresSpeciauxCouleurs[char]) {
            const color = caracteresSpeciauxCouleurs[char];
            styledHTML += `<span style="color: ${color}">${char}</span>`;
            hasColors = true;
          }
          // Vérifier si c'est une lettre avec couleur
          else if (lettresCouleurs[charUpper] || lettresCouleurs[charLower]) {
            const color = lettresCouleurs[charUpper] || lettresCouleurs[charLower];
            styledHTML += `<span style="color: ${color}">${char}</span>`;
            hasColors = true;
          }
          // Vérifier si c'est un chiffre avec couleur
          else if (chiffresCouleurs[char]) {
            styledHTML += `<span style="color: ${chiffresCouleurs[char]}">${char}</span>`;
            hasColors = true;
          }
          else {
            styledHTML += char;
          }
        }
        
        // Remplacer le nœud texte si des couleurs ont été appliquées
        if (hasColors) {
          const span = document.createElement('span');
          span.innerHTML = styledHTML;
          node.parentNode.replaceChild(span, node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Parcourir récursivement les enfants
        Array.from(node.childNodes).forEach(colorTextNodes);
      }
    };
    
    colorTextNodes(tempDiv);
    return tempDiv.innerHTML;
  };

  const handleSave = async () => {
    const wordCount = getWordCount(content);
    if (wordCount > WORD_LIMIT) {
      alert(t('document.wordLimitExceeded') || `Limite de ${WORD_LIMIT} mots dépassée.`);
      return;
    }
    setLoading(true);
    setSaved(false);
    try {
      const data = {
        titre: title || 'Sans titre',
        contenu: content,
        matrice_utilisee: matrice ? matrice.id : null,
      };

      if (id) {
        // Modification d'un document existant
        await api.put(`/documents/${id}/`, data);
      } else {
        // Création d'un nouveau document
        await api.post('/documents/', data);
      }
      
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (id) {
          // Rediriger vers l'historique après modification
          navigate('/document-history');
        }
      }, 1500);
    } catch (error) {
      console.error('Error saving document:', error);
      alert(t('document.saveError') || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    setShowExportModal(true);
  };

  const executeExportPDF = async (includeBackground) => {
    setLoading(true);
    setShowExportModal(false);
    
    try {
      // Créer un élément temporaire pour le rendu
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = applyColorsToText(content);
      tempDiv.style.padding = '20px';
      tempDiv.style.fontSize = '14px';
      
      // Appliquer l'arrière-plan si demandé
      if (includeBackground) {
        tempDiv.style.backgroundColor = previewBackgroundColor;
      }
      
      document.body.appendChild(tempDiv);

      // Convertir en canvas puis en PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        backgroundColor: includeBackground ? previewBackgroundColor : null,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Si on inclut l'arrière-plan, ajouter un rectangle de fond sur chaque page
      if (includeBackground) {
        // Convertir la couleur hex en RGB pour jsPDF
        const rgb = hexToRgb(previewBackgroundColor);
        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.rect(0, 0, imgWidth, pageHeight, 'F');
      }

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        
        // Ajouter l'arrière-plan sur chaque nouvelle page
        if (includeBackground) {
          const rgb = hexToRgb(previewBackgroundColor);
          pdf.setFillColor(rgb.r, rgb.g, rgb.b);
          pdf.rect(0, 0, imgWidth, pageHeight, 'F');
        }
        
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${title || 'document'}.pdf`);
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erreur lors de l\'export PDF');
    } finally {
      setLoading(false);
    }
  };

  // Fonction utilitaire pour convertir hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  return (
    <div className="document-editor">
      <Navbar />

      <div className="editor-content">
        <div className="editor-actions">
          <input
            type="text"
            placeholder="Titre du document"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="title-input"
          />
          <select
            value={matrice?.id || ''}
            onChange={handleMatriceChange}
            className="matrice-selector"
            disabled={matrices.length === 0}
          >
            {matrices.length === 0 ? (
              <option value="">Aucune matrice</option>
            ) : (
              matrices.map((m) => (
                <option key={m.id} value={m.id}>
                  Matrice #{m.id} - {m.langue?.toUpperCase() || 'FR'}
                </option>
              ))
            )}
          </select>
          <button 
            onClick={() => setShowKeyboard(true)} 
            className="keyboard-button"
            disabled={!matrice}
            title={t('document.keyboard')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M6 8h.01"/>
              <path d="M10 8h.01"/>
              <path d="M14 8h.01"/>
              <path d="M18 8h.01"/>
              <path d="M8 12h.01"/>
              <path d="M12 12h.01"/>
              <path d="M16 12h.01"/>
              <path d="M7 16h10"/>
            </svg>
            <span>{t('document.keyboard')}</span>
          </button>
          <span className={`word-count ${getWordCount(content) >= WORD_LIMIT ? 'word-count-limit' : ''}`}>
            {getWordCount(content)} / {WORD_LIMIT} {getWordCount(content) === 1 ? (t('document.wordCountOne') || 'mot') : (t('document.wordCount') || 'mots')}
          </span>
          <div className="spacer"></div>
          <button onClick={handleSave} disabled={loading || getWordCount(content) > WORD_LIMIT} className="save-button">
            {loading ? 'Enregistrement...' : t('document.save')}
          </button>
          <button onClick={handleExportPDF} disabled={loading} className="export-button">
            {t('document.exportPDF')}
          </button>
          {saved && <span className="saved-message">✓ Enregistré!</span>}
        </div>
        {!loadingMatrices && matrices.length === 0 && (
          <div className="warning-message">
            Aucune matrice trouvée. Veuillez créer une matrice d'abord.
          </div>
        )}
        <div className="editor-split-container">
          <div className="editor-split-inner">
          <div className="editor-section">
            <div className="quill-container">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={handleContentChange}
                modules={modules}
                placeholder="Commencez à écrire..."
              />
            </div>
          </div>
          <div className="preview-section">
            <div className="preview-header">
              <h3>{t('document.previewWithColors')}</h3>
              <div className="preview-background-picker">
                <label htmlFor="preview-bg-color" className="color-picker-label">
                  {t('document.background')}
                </label>
                <input
                  id="preview-bg-color"
                  type="color"
                  value={previewBackgroundColor}
                  onChange={(e) => setPreviewBackgroundColor(e.target.value)}
                  className="background-color-picker"
                  title={t('document.background')}
                />
              </div>
            </div>
            <div
              className="preview-content"
              style={{ backgroundColor: previewBackgroundColor }}
              dangerouslySetInnerHTML={{ __html: applyColorsToText(content) }}
            />
          </div>
          </div>
        </div>
        
        {showKeyboard && matrice && (
          <VirtualKeyboard
            language={matrice.langue || 'fr'}
            matrice={matrice}
            onClose={() => setShowKeyboard(false)}
            onCharacterClick={handleCharacterClick}
          />
        )}

        {showExportModal && (
          <div className="export-modal-overlay" onClick={() => setShowExportModal(false)}>
            <div className="export-modal" onClick={(e) => e.stopPropagation()}>
              <div className="export-modal-header">
                <h3>Options d'export PDF</h3>
                <button className="export-modal-close" onClick={() => setShowExportModal(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="export-modal-content">
                <div className="export-option">
                  <label className="export-option-label">
                    <input
                      type="checkbox"
                      id="include-background"
                      defaultChecked={false}
                    />
                    <span>Inclure l'arrière-plan</span>
                  </label>
                  <div className="background-preview" style={{ backgroundColor: previewBackgroundColor }}>
                    <span>Aperçu</span>
                  </div>
                </div>
              </div>
              <div className="export-modal-actions">
                <button 
                  className="export-modal-cancel" 
                  onClick={() => setShowExportModal(false)}
                >
                  Annuler
                </button>
                <button 
                  className="export-modal-confirm" 
                  onClick={() => {
                    const includeBackground = document.getElementById('include-background').checked;
                    executeExportPDF(includeBackground);
                  }}
                >
                  Exporter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentEditor;

